import { useEffect, useMemo, useReducer, useRef } from "react";
import S from "@/lib/sound";
import { serverNow } from "@/lib/api";
import { prefersReducedMotion } from "@/lib/motion";
import { revealMsFor, timingFrom } from "@/lib/weatherTiming";

/**
 * WEATHER RUNS BY ITSELF — one timeline per weather event, the same on every screen
 * (server clock, from weather_state.drawn_at):
 *   flipping → result (weather card shown) → global (the global card flips next to it)
 *   → reveal (overlay fades, area reveal on the board) → results.
 * Shape faces: the reveal is the AREA SHUFFLE (ws.shuffle, the same decoys at the same server
 * time on every screen), then the real area freezes; the results follow FREEZE_BEAT later.
 * A screen that loads late joins the timeline where it is now; a screen that first sees the
 * weather during (or after) the reveal shows the final area at once (no shuffle). A save
 * without a draw time (or a page opened long after) goes straight to "results".
 *
 * Returns { key, phase, overlay, overlayFading, boardArea, animateArea, shuffle, resultsShown,
 *           revealEndMs, drawnAtMs, offsets { weatherFlip, globalFlip } (ms from the draw, for the
 *           card flips), reduced }.
 */
export function useWeatherTimeline(game, faces) {
  const T = useMemo(() => timingFrom(faces), [faces]);
  const ws = game?.phase === "weather" ? game.weather_state : null;
  const drawn = ws?.drawn_at ? Date.parse(ws.drawn_at) : NaN;
  const key = Number.isFinite(drawn) ? `${game.id}|${game.round}|${ws.drawn_at}` : null;
  const [, bump] = useReducer((x) => x + 1, 0);
  const seenRef = useRef({ key: null, liveShuffle: false, live: false });
  const soundRef = useRef(null);
  const reduced = prefersReducedMotion();

  const globalAt = T.FLIP + T.RESULT_HOLD; // the global card starts to flip
  const overlayEnd = globalAt + T.GLOBAL_CARD; // the overlay leaves at the same server time on every screen
  const revealEnd = overlayEnd + revealMsFor(T, ws);
  const hasShuffle = ws?.kind === "shape" && (ws?.shuffle?.decoys?.length || 0) > 0;
  const elapsed = Number.isFinite(drawn) ? serverNow() - drawn : Infinity;

  // Decided once per draw: did this screen see the cards come out, and does it show the shuffle?
  if (key && seenRef.current.key !== key) {
    seenRef.current = {
      key,
      live: elapsed < T.FLIP,
      // The shuffle runs only on a screen that saw the weather before the reveal began.
      liveShuffle: hasShuffle && elapsed < overlayEnd,
    };
  }
  const liveShuffle = !!seenRef.current.liveShuffle && hasShuffle;
  const resultsAt = revealEnd + (liveShuffle ? T.FREEZE_BEAT : 0);

  let phase = "none";
  if (ws) {
    if (!key) phase = "results";
    else if (elapsed < T.FLIP) phase = "flipping";
    else if (elapsed < globalAt) phase = "result";
    else if (elapsed < overlayEnd) phase = "global";
    else if (elapsed < resultsAt) phase = "reveal";
    else phase = "results";
  } else if (game?.phase === "weather" && game?.mode === "main") {
    phase = "flipping"; // waiting for the server's draw (a moment at most)
  }

  // Re-render at each boundary of the timeline.
  useEffect(() => {
    if (!key || !Number.isFinite(drawn)) return undefined;
    const now = serverNow() - drawn;
    const marks = [T.FLIP, globalAt, overlayEnd, overlayEnd + T.OVERLAY_FADE, revealEnd, resultsAt].filter((m) => m > now);
    const timers = marks.map((m) => setTimeout(bump, m - now + 20));
    return () => timers.forEach(clearTimeout);
  }, [key, T.FLIP, globalAt, overlayEnd, revealEnd, resultsAt]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sounds, only on screens that saw the cards come out: a card-flip sound for each card, and the
  // face's sound once the weather card is shown. With the area shuffle the face's sound comes after
  // the freeze thud instead (WeatherArea plays both).
  useEffect(() => {
    if (!key || !seenRef.current.live || soundRef.current === `${key}|${phase}`) return;
    if (phase === "flipping" || phase === "global") {
      soundRef.current = `${key}|${phase}`;
      S.cardFlip();
    } else if (phase === "result" && !liveShuffle) {
      soundRef.current = `${key}|${phase}`;
      S.weatherResult(ws?.face);
    }
  }, [phase, key]); // eslint-disable-line react-hooks/exhaustive-deps

  const overlay = phase === "flipping" || phase === "result" || phase === "global" || (phase === "reveal" && elapsed < overlayEnd + T.OVERLAY_FADE);
  return {
    key,
    phase,
    timing: T,
    reduced,
    overlay,
    overlayFading: phase === "reveal",
    boardArea: phase === "reveal" || phase === "results",
    animateArea: phase === "reveal",
    // AREA SHUFFLE on the board: live = this screen shows it; startMs = reveal start (server ms).
    shuffle: hasShuffle ? { live: liveShuffle, startMs: Number.isFinite(drawn) ? drawn + overlayEnd : 0, reduced } : null,
    resultsShown: phase === "results",
    drawing: phase === "flipping" || phase === "result" || phase === "global",
    revealEndMs: Number.isFinite(drawn) ? drawn + revealEnd : 0,
    drawnAtMs: Number.isFinite(drawn) ? drawn : 0,
    // When each card starts to flip, ms from the draw (the flip itself takes about half a second).
    offsets: { weatherFlip: 150, globalFlip: globalAt },
    live: !!seenRef.current.live,
  };
}

export default useWeatherTimeline;
